/**
 * 创作中心画布 · 只读预览（复用 StudioNode / StudioEdge，与编辑态视觉一致）
 */
import { useEffect, useMemo, useRef } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '@/features/studio/styles/canvas-base.css'
import '@/features/studio/styles/canvas-node-layout.css'
import '@/features/studio/canvas/theme/node-themes.css'
import '@/features/studio/canvas/theme/edge-anims.css'

import type { AITaskType } from '@/api/types'
import { CanvasMiniMap } from '@/features/studio/canvas/CanvasMiniMap'
import { CanvasViewportControls } from '@/features/studio/canvas/CanvasViewportControls'
import {
  CanvasNodeProvider,
  type CanvasNodeContextValue,
} from '@/features/studio/canvas/CanvasNodeContext'
import { StudioEdge } from '@/features/studio/canvas/edges/StudioEdge'
import { GroupNode } from '@/features/studio/canvas/nodes/GroupNode'
import { StudioNode } from '@/features/studio/canvas/nodes/StudioNode'
import {
  prepareStudioGraphFromServer,
  type ServerGraphInput,
} from '@/features/studio/canvas/hydrate-graph'
import { CanvasThemeProvider, useCanvasTheme } from '@/features/studio/canvas/theme/CanvasThemeContext'
import { useAIModels } from '@/features/studio/hooks/useAIModels'
import { modelMatchesTaskType } from '@/shared/hooks/useModels'
import { cn } from '@/shared/lib/utils'

const nodeTypes = { studio: StudioNode, group: GroupNode }
const edgeTypes = { studio: StudioEdge }

const noop = () => {}

function graphHydrateKey(graph?: ServerGraphInput, modelsLen = 0): string {
  const nodes = graph?.nodes
  const edges = graph?.edges
  const n = Array.isArray(nodes) ? nodes.length : 0
  const e = Array.isArray(edges) ? edges.length : 0
  const vp = graph?.viewport
  const vz = typeof vp?.zoom === 'number' ? vp.zoom : 0
  return `${n}:${e}:${vz}:${modelsLen}`
}

function ReadOnlyFlowInner({
  graph,
  className,
  hydrateKey,
}: {
  graph?: ServerGraphInput
  className?: string
  hydrateKey: string
}) {
  const { models: allModels } = useAIModels()
  const { nodeTheme, edgeAnim } = useCanvasTheme()
  const rf = useReactFlow()

  const prepared = useMemo(() => {
    const audioModels = allModels.filter((m) => modelMatchesTaskType(m, 'audio'))
    const defaultModelId = (type: AITaskType) =>
      allModels.filter((m) => modelMatchesTaskType(m, type))[0]?.id ?? ''
    return prepareStudioGraphFromServer(graph, { defaultModelId, audioModels })
  }, [graph, allModels])

  const [nodes, , onNodesChange] = useNodesState(prepared.nodes)
  const [edges, , onEdgesChange] = useEdgesState(prepared.edges)

  const viewportApplied = useRef(false)
  useEffect(() => {
    viewportApplied.current = false
  }, [hydrateKey])

  useEffect(() => {
    if (viewportApplied.current || prepared.nodes.length === 0) return
    viewportApplied.current = true
    requestAnimationFrame(() => {
      if (prepared.viewport) {
        void rf.setViewport(prepared.viewport, { duration: 0 })
        return
      }
      void rf.fitView({ padding: 0.15, duration: 0 })
    })
  }, [hydrateKey, prepared.nodes.length, prepared.viewport, rf])

  const nodeContextValue = useMemo((): CanvasNodeContextValue => {
    const imageModels = allModels.filter((m) => modelMatchesTaskType(m, 'image'))
    const videoModels = allModels.filter((m) => modelMatchesTaskType(m, 'video'))
    const audioModels = allModels.filter((m) => modelMatchesTaskType(m, 'audio'))
    const textModels = allModels.filter((m) => modelMatchesTaskType(m, 'text'))
    return {
      aspectRatio: '16:9',
      imageModels,
      videoModels,
      audioModels,
      textModels,
      onRun: noop,
      onPatchDraft: noop,
      onPatch: noop,
      onFocusUpstream: noop,
      onPreferTake: noop,
      onUploadFile: noop,
    }
  }, [allModels])

  return (
    <div
      className={cn(
        'studio-canvas-dots studio-canvas-readonly relative h-full min-h-0 w-full overflow-hidden',
        className,
      )}
      data-node-theme={nodeTheme}
      data-edge-anim={edgeAnim}
      data-readonly
    >
      <CanvasNodeProvider value={nodeContextValue}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: 'studio', data: { animated: true }, interactionWidth: 24 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          connectOnClick={false}
          panOnDrag
          zoomOnScroll
          minZoom={0.15}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          noWheelClassName="nowheel"
          noPanClassName="nopan"
          noDragClassName="nodrag"
          className="studio-canvas-flow !bg-transparent"
        >
          <CanvasViewportControls />
          <CanvasMiniMap />
        </ReactFlow>
      </CanvasNodeProvider>
    </div>
  )
}

export function StudioCanvasReadOnly({
  graph,
  className,
}: {
  graph?: ServerGraphInput
  className?: string
}) {
  const { models: allModels } = useAIModels()
  const hydrateKey = graphHydrateKey(graph, allModels.length)

  return (
    <ReactFlowProvider>
      <CanvasThemeProvider>
        <ReadOnlyFlowInner
          key={hydrateKey}
          hydrateKey={hydrateKey}
          graph={graph}
          className={className}
        />
      </CanvasThemeProvider>
    </ReactFlowProvider>
  )
}
